import { useEffect, useState } from "react";
import Image from "next/image";
import { IoClose } from "react-icons/io5";
import PhoneInputField from "@/Common/PhoneInputField";
import { useAlert } from "@/Common/CustomAlert/AlertContext";
import { submitDownloadItineraryForm } from "@/services/api/holidays";
import { isIndianUser } from "@/utils/getCountryCode";
import { parsePhoneForApi, validatePhoneValue } from "@/utils/phoneInput";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import styles from "./DownloadItineraryModal.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function DownloadItineraryModal({
  open,
  onClose,
  packageId,
  packageName = "",
  itineraryText = "",
  nights = 0,
  image = "",
  badgeLabel = "",
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: undefined,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { showAlert } = useAlert();

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    setForm({
      name: "",
      email: "",
      phone: undefined,
    });
    setErrors({});
    setLoading(false);
    setSubmitted(false);

    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Full name is required";
    if (!form.email.trim()) next.email = "Email is required";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email";

    const phoneError = validatePhoneValue(form.phone);
    if (phoneError) next.phone = phoneError;

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhoneChange = (value) => {
    setForm((prev) => ({ ...prev, phone: value }));
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const parsedPhone = parsePhoneForApi(form.phone);
    if (!parsedPhone) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid mobile number" }));
      return;
    }

    const isIndia = isIndianUser();
    const payload = {
      id: 0,
      name: form.name.trim(),
      email: form.email.trim(),
      phoneNo: parsedPhone.phoneNo,
      countryCode: parsedPhone.countryCode,
      packageId: Number(packageId) || 0,
      createdDate: new Date().toISOString(),
      countryEnum: isIndia ? 0 : 1,
      url: typeof window !== "undefined" ? window.location.href : "",
    };

    setLoading(true);
    try {
      const response = await submitDownloadItineraryForm(payload);
      const status = String(response?.status || response?.Status || "").toLowerCase();
      if (status && status !== "success") {
        throw new Error(response?.message || "Submission failed");
      }
      setSubmitted(true);
    } catch (err) {
      showAlert({
        title: "Submission failed",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to submit request. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const days = nights ? nights + 1 : 0;
  const pillLabel = badgeLabel || (nights ? `${nights}N/${days}D` : "");

  return (
    <div className={styles.root} role="presentation">
      <button
        type="button"
        className={styles.overlay}
        aria-label="Close download itinerary modal"
        onClick={onClose}
      />
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={submitted ? "Itinerary request submitted" : "Download holiday itinerary"}
      >
        <button
          type="button"
          className={`${styles.closeBtn} ${styles.closeBtnAbsolute}`}
          aria-label="Close"
          onClick={onClose}
        >
          <IoClose size={22} />
        </button>

        {submitted ? (
          <div className={styles.success}>
            <Image
              src="/assets/icons/enquiry-success.svg"
              alt=""
              width={120}
              height={98}
              className={styles.successIcon}
              quality={70}
              loading="lazy"
            />
            <h2 className={styles.successTitle}>You&apos;re All Set!</h2>
            <p className={styles.successMessage}>
              Your itinerary request has been submitted. Our experts will contact
              you shortly with the details.
            </p>
            <button type="button" className={styles.submitBtn} onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
        <div className={styles.header}>
          <div className={styles.packageRow}>
            <div className={styles.thumb}>
              {image ? <img src={image} alt={packageName || "Package"} /> : null}
            </div>
            <div className={styles.packageInfo}>
              <h2 className={styles.title}>{packageName}</h2>
              {itineraryText ? <p className={styles.itinerary}>{itineraryText}</p> : null}
              {pillLabel ? <span className={styles.slab}>{pillLabel}</span> : null}
            </div>
          </div>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Full Name*</span>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
              placeholder="Enter Full Name"
            />
            {errors.name ? <span className={styles.errorText}>{errors.name}</span> : null}
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Email ID*</span>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
              placeholder="Enter Email ID"
            />
            {errors.email ? <span className={styles.errorText}>{errors.email}</span> : null}
          </label>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Mobile Number*</span>
            <PhoneInputField
              name="phone"
              value={form.phone}
              onChange={handlePhoneChange}
              error={Boolean(errors.phone)}
            />
            {errors.phone ? (
              <span className={styles.errorText}>{errors.phone}</span>
            ) : null}
          </div>

          <button type="submit" className={styles.submitBtn} disabled={loading}>
            {loading ? "Submitting..." : "Download Now"}
          </button>
        </form>
          </>
        )}
      </div>
    </div>
  );
}
